/**
 * Site-dashboard alarm/maintenance card placement — decides which of the
 * dashboard's three surfaces those two summary cards belong on at the current
 * width and subpage.
 *
 * On a desktop they belong beside the site, in the context rail. On a phone
 * looking at the site's own dashboard they are the reason the page was opened,
 * so they sit in the page itself rather than behind a drawer the reader has to
 * know to open. Everywhere else — a tablet, or a subpage whose own content owns
 * the stage — they stay in the details drawer with the rest of the rail.
 *
 * Exactly one placement is returned, so the cards are never rendered twice.
 *
 * Contains:
 * - RAIL_EXTRAS_PLACEMENT — the three surfaces
 * - resolveRailExtrasPlacement — the pure width/subpage → surface decision
 *
 * Returns plain JS — no JSX.
 */

export const RAIL_EXTRAS_PLACEMENT = Object.freeze({
  /** Stacked under the sticky context sidebar (wide viewports). */
  RAIL: 'rail',
  /** Inside the slide-out details drawer (compact viewports). */
  DRAWER: 'drawer',
  /** Inline in the page flow, under the main stage (phone, dashboard index). */
  STAGE: 'stage',
});

/**
 * @param {object} params
 * @param {boolean} params.isCompact - true below the width where the rail fits
 *   beside the stage, i.e. the rail has detached into the drawer.
 * @param {boolean} params.isPhone - true at phone width.
 * @param {boolean} params.inlineOnPhone - true when the current subpage wants
 *   the cards in its own flow on a phone (the dashboard index does; subpages
 *   with their own content do not).
 * @param {string} params.stageScroll - `'page'` when the stage grows with its
 *   content, `'contained'` when it is bounded to the viewport.
 * @returns {string} One `RAIL_EXTRAS_PLACEMENT` value.
 */
export function resolveRailExtrasPlacement({ isCompact, isPhone, inlineOnPhone, stageScroll }) {
  if (!isCompact) return RAIL_EXTRAS_PLACEMENT.RAIL;
  // A contained stage is bounded to the viewport, so anything appended under it
  // is off-screen with no way to scroll to it — those subpages keep the drawer.
  if (isPhone && inlineOnPhone && stageScroll !== 'contained') return RAIL_EXTRAS_PLACEMENT.STAGE;
  return RAIL_EXTRAS_PLACEMENT.DRAWER;
}
